import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./config/firebase";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// Normalize role strings from Firestore to the canonical set.
// "customer" (old default) maps to "client". "superadmin" (old lowercase) maps to "superAdmin".
function normalizeRole(role) {
  if (!role) return "client";
  const r = role.toLowerCase();
  if (r === "superadmin") return "superAdmin";
  if (r === "admin")      return "admin";
  if (r === "staff")      return "staff";
  if (r === "vip")        return "vip";
  if (r === "user")       return "user";
  if (r === "vendor")     return "vendor";
  if (r === "partner")    return "partner";
  return "client"; // "customer" and any unknown value → client
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole]       = useState(null);
  const [authReady, setAuthReady]     = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          const raw = userDoc.exists() ? userDoc.data().role : null;
          setUserRole(normalizeRole(raw));
          setCurrentUser(user);
        } else {
          setCurrentUser(null);
          setUserRole(null);
        }
      } catch (error) {
        console.error("AuthContext error:", error);
        setCurrentUser(user);
        setUserRole("client");
      } finally {
        setAuthReady(true);
      }
    });

    return unsubscribe;
  }, []);

  const logout = () => auth.signOut();

  const value = {
    currentUser,
    userRole,
    // true for both admin and superAdmin — use for dashboard access, content editing, etc.
    isAdmin: userRole === "admin" || userRole === "superAdmin",
    // true only for superAdmin — use for user management and role changes
    isSuperAdmin: userRole === "superAdmin",
    logout,
    authReady,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
